/** The launch/click vocabulary is ported from Desktop renderer.js (brand audio).
 * Atmosphere uses the author's trailer stems; mechanical Foley is licensed CC0.
 * This graph also runs in OfflineAudioContext for reproducible stem checks. */
import {AUDIO_SOURCES,TRAILER_SOURCES,type TrailerSource} from './trailer';
export type SoundKind='start'|'stop'|'tap'|'check'|'air'|'title'|'cube'|'spring'|'squash'|'recover'|'assemble'|'shutter'|'switch'|'surprise';
export interface SoundPosition{x:number;y:number;z:number}
export interface SoundCue {kind:SoundKind;gain?:number;pan?:number;duration?:number;pitch?:number;sample?:TrailerSource;position?:SoundPosition;follow?:boolean;sustain?:boolean;delay?:number;offset?:number;clipDuration?:number;fadeIn?:number;fadeOut?:number;elapsed?:number}
interface SamplePlayback {
 cue:SoundCue;buffer:AudioBuffer;offset:number;rate:number;duration:number;
 gain:number;attack:number;release:number;elapsed:number;at:number;
}
const EPS=.0001;
export const MASTER_GAIN=3.125; // Restore .32-gain reference recordings to source level.
const clamp=(v:number,a:number,b:number)=>Math.max(a,Math.min(b,v));
export class BrandVoices {
 readonly master:GainNode;
 readonly ambience:GainNode;
 private readonly effects:GainNode;
 private readonly reverb:ConvolverNode;
 private readonly nodes=new Set<AudioNode>();
 private readonly sources=new Set<AudioScheduledSourceNode>();
 private readonly voiceGains=new Map<AudioScheduledSourceNode,GainNode[]>();
 private readonly samplePlaybacks=new Map<AudioScheduledSourceNode,SamplePlayback>();
 private pausedSamples:SamplePlayback[]=[];
 private paused=false;
 private readonly bed:AudioScheduledSourceNode[]=[];
 private randomState=0x42425247;
 private readonly buffers=new Map<number,AudioBuffer>();
 private ambienceLevel=-1;
 private ducked=false;
 private muted=false;
 private readonly samples=new Map<TrailerSource,AudioBuffer>();
 private disposed=false;
 private currentCue:SoundCue|null=null;
 private readonly movingPanners=new Set<PannerNode>();
 private readonly loading=new Map<TrailerSource,Promise<void>>();
 private readonly requests=new AbortController();
 private readonly bedFilter:BiquadFilterNode;
 private readonly bedModulation:OscillatorNode;
 async loadSources(){
  const results=await Promise.allSettled(AUDIO_SOURCES.map(key=>{
   if(this.samples.has(key)||this.disposed)return Promise.resolve();
   const pending=this.loading.get(key);if(pending)return pending;
   const job=this.loadSource(key).finally(()=>this.loading.delete(key));this.loading.set(key,job);return job;
  }));
  if(!this.samples.has('logo_ambient_89')&&!this.disposed)throw new Error('Atmosphere unavailable');
  return results;
 }
 private async loadSource(key:TrailerSource){
  let response:Response|undefined;
  for(let attempt=0;attempt<3;attempt++){
   try{
    response=await fetch(`/assets/audio/${key}.mp3`,{cache:'no-cache',signal:this.requests.signal});
    if(response.ok)break;
    throw new Error(`Audio source unavailable: ${key}`);
   }catch(error){
    if(this.disposed||attempt===2)throw error;
    await new Promise(resolve=>setTimeout(resolve,300*(attempt+1)));
   }
  }
  if(!response?.ok||this.disposed)return;
   const buffer=await this.context.decodeAudioData(await response.arrayBuffer());
   if(this.disposed)return;this.samples.set(key,buffer);
   if(key==='logo_ambient_89'){
    // Remove the encoded-stem boundary without silencing the musical bed.
    // A short correlated crossfade reaches the exact sample preceding loop start.
    const start=Math.round(21.6*buffer.sampleRate),end=Math.round(75.6*buffer.sampleRate),fade=Math.round(4*buffer.sampleRate),length=end-start-fade;
    if(end>buffer.length||length<=fade){this.samples.delete(key);throw new Error('Atmosphere source has an invalid loop range');}
    const loop=this.context.createBuffer(buffer.numberOfChannels,length,buffer.sampleRate);
    for(let channel=0;channel<buffer.numberOfChannels;channel++){
     const input=buffer.getChannelData(channel),output=loop.getChannelData(channel);output.set(input.subarray(start+fade,end));
     for(let i=0;i<fade;i++){const weight=i/(fade-1),index=length-fade+i;output[index]=output[index]!*(1-weight)+input[start+i]!*weight;}
    }
    const source=this.context.createBufferSource();source.buffer=loop;source.loop=true;
    source.connect(this.bedFilter);source.start(this.context.currentTime);this.bed.push(source);this.nodes.add(source);
   }
 }
 constructor(readonly context:BaseAudioContext,destination:AudioNode=context.destination){
  this.master=context.createGain();this.master.gain.value=MASTER_GAIN;
  this.effects=context.createGain();this.ambience=context.createGain();this.ambience.gain.value=0;
  // Slow spectral breathing reshapes the same authored musical recording,
  // without detuning it, adding a drone, or audibly pumping its loudness.
  this.bedFilter=context.createBiquadFilter();this.bedFilter.type='lowpass';this.bedFilter.frequency.value=1750;this.bedFilter.Q.value=.35;this.bedFilter.connect(this.ambience);
  this.bedModulation=context.createOscillator();this.bedModulation.type='sine';this.bedModulation.frequency.value=1/37;
  const colorDepth=context.createGain();colorDepth.gain.value=650;this.bedModulation.connect(colorDepth);colorDepth.connect(this.bedFilter.frequency);this.bedModulation.start();
  for(const node of [this.bedFilter,this.bedModulation,colorDepth])this.nodes.add(node);
  // Headroom is in the voices. The compressor catches overlaps, not loudness-maximizes them.
  const safety=context.createDynamicsCompressor();safety.threshold.value=-1.5;safety.knee.value=1;safety.ratio.value=20;safety.attack.value=.003;safety.release.value=.2;
  this.effects.connect(this.master);this.ambience.connect(this.master);this.master.connect(safety);safety.connect(destination);
  this.reverb=context.createConvolver();const ir=context.createBuffer(2,Math.round(context.sampleRate*2.2),context.sampleRate);
  for(let c=0;c<2;c++){const data=ir.getChannelData(c);for(let i=0;i<data.length;i++)data[i]=this.random()*(1-i/data.length)**2.5;}
  this.reverb.buffer=ir;const wet=context.createGain();wet.gain.value=.3;this.reverb.connect(wet);wet.connect(this.effects);
  for(const n of [this.master,this.effects,this.ambience,safety,this.reverb,wet])this.nodes.add(n);
 }
 private random(){this.randomState^=this.randomState<<13;this.randomState^=this.randomState>>>17;this.randomState^=this.randomState<<5;return(this.randomState>>>0)/2147483648-1;}
 private noise(seconds:number,decay=true){
  const length=Math.round(seconds*this.context.sampleRate),key=decay?length:-length;let buffer=this.buffers.get(key);
  if(!buffer){buffer=this.context.createBuffer(1,length,this.context.sampleRate);const data=buffer.getChannelData(0);for(let i=0;i<length;i++)data[i]=this.random()*(decay?1-i/length:1);this.buffers.set(key,buffer);}
  return buffer;
 }
 private track(source:AudioScheduledSourceNode,nodes:AudioNode[],at:number,end:number,start=(time:number)=>source.start(time)){
  this.sources.add(source);for(const node of nodes)this.nodes.add(node);
  this.voiceGains.set(source,nodes.filter((node):node is GainNode=>'gain' in node));
  source.onended=()=>{source.disconnect();this.sources.delete(source);this.voiceGains.delete(source);this.samplePlaybacks.delete(source);for(const node of nodes){if('positionX' in node)this.movingPanners.delete(node as PannerNode);node.disconnect();this.nodes.delete(node);}};
  start(at);source.stop(end);
 }
 private panner(pan:number,t:number){
  const position=this.currentCue?.position;
  if(!position){const p=this.context.createStereoPanner();p.pan.value=clamp(pan,-.85,.85);return p;}
  const p=this.context.createPanner();p.panningModel='HRTF';p.distanceModel='inverse';p.refDistance=3;p.maxDistance=12;p.rolloffFactor=.15;
  p.positionX.setValueAtTime(position.x,t);p.positionY.setValueAtTime(position.y,t);p.positionZ.setValueAtTime(position.z,t);
  if(this.currentCue?.follow)this.movingPanners.add(p);return p;
 }
 setPosition(position:SoundPosition){const t=this.context.currentTime;for(const p of this.movingPanners){p.positionX.setTargetAtTime(position.x,t,.035);p.positionY.setTargetAtTime(position.y,t,.035);p.positionZ.setTargetAtTime(position.z,t,.035);}}
 private envelope(g:AudioParam,t:number,dur:number,vol:number,attack:number,brand=false,hold=.3){
  g.setValueAtTime(EPS,t);
  if(brand)g.linearRampToValueAtTime(Math.max(EPS,vol),t+Math.min(attack,dur*.8));
  else g.exponentialRampToValueAtTime(Math.max(EPS,vol),t+Math.min(attack,dur*.8));
  if(brand&&dur>attack*2)g.setValueAtTime(vol*.9,t+dur*hold);
  g.exponentialRampToValueAtTime(EPS,t+dur);
 }
 private tone(freq:number,type:OscillatorType,t:number,dur:number,vol:number,attack=.003,cutoff=6000,pan=0,wet=0,to=freq,filterTo=cutoff,q=.7,brand=false){
  const ac=this.context,o=ac.createOscillator(),filter=ac.createBiquadFilter(),gain=ac.createGain(),panner=this.panner(pan,t);
  o.type=type;o.frequency.setValueAtTime(freq,t);if(to!==freq)o.frequency.exponentialRampToValueAtTime(to,t+dur*.7);
  filter.type='lowpass';filter.frequency.setValueAtTime(cutoff,t);filter.Q.value=q;if(filterTo!==cutoff)filter.frequency.exponentialRampToValueAtTime(filterTo,t+dur*.7);
  this.envelope(gain.gain,t,dur,vol,attack,brand,filterTo!==cutoff?.4:.3);
  o.connect(filter);filter.connect(gain);gain.connect(panner);panner.connect(this.effects);
  const nodes:AudioNode[]=[filter,gain,panner];
  if(wet){const send=ac.createGain();send.gain.value=wet;gain.connect(send);send.connect(this.reverb);nodes.push(send);}
  this.track(o,nodes,t,t+dur+.05);
 }
 private noiseVoice(t:number,dur:number,vol:number,from:number,to:number,pan:number,attack=.03,type:BiquadFilterType='bandpass',envelopeDuration=dur){
  const ac=this.context,s=ac.createBufferSource(),filter=ac.createBiquadFilter(),gain=ac.createGain(),panner=this.panner(pan,t);
  s.buffer=this.noise(dur);filter.type=type;filter.Q.value=.35;filter.frequency.setValueAtTime(from,t);filter.frequency.exponentialRampToValueAtTime(to,t+dur*.8);
  this.envelope(gain.gain,t,envelopeDuration,vol,attack);
  s.connect(filter);filter.connect(gain);gain.connect(panner);panner.connect(this.effects);this.track(s,[filter,gain,panner],t,t+dur+.02);
 }
 private click(t:number,vol:number,pan:number){this.noiseVoice(t,.018,vol,2600,2600,pan,.003,'highpass',.063);this.tone(2600,'sine',t+.002,.052,vol*.35,.002,12000,pan);}
 private playSample(playback:SamplePlayback,at:number,resuming=false){
  const {buffer,offset,rate,duration,gain:volume,attack,release,elapsed,cue}=playback;
  const remaining=duration-elapsed;if(remaining<=0||this.disposed)return;
  this.currentCue=cue;
  const source=this.context.createBufferSource(),gain=this.context.createGain(),fade=this.context.createGain(),panner=this.panner(cue.pan??0,at);
  source.buffer=buffer;source.playbackRate.value=rate;
  const releaseAt=duration-release;
  const level=elapsed<attack?volume*elapsed/attack:elapsed>releaseAt&&release>0?volume*(duration-elapsed)/release:volume;
  gain.gain.setValueAtTime(level,at);
  if(elapsed<attack)gain.gain.linearRampToValueAtTime(volume,at+attack-elapsed);
  if(elapsed<releaseAt)gain.gain.setValueAtTime(volume,at+releaseAt-elapsed);
  gain.gain.linearRampToValueAtTime(0,at+remaining);
  // A short independent fade reconnects the saved envelope without resetting it.
  if(resuming){fade.gain.setValueAtTime(0,at);fade.gain.linearRampToValueAtTime(1,at+Math.min(.012,remaining));}
  // A long flight is one resumable noise source. A constant rounded spectrum
  // avoids a metallic sweep; the envelope and moving actor provide its motion.
  const typing=cue.sample?.startsWith('foley_key');
  const filter=typing||cue.kind==='air'&&cue.sustain?this.context.createBiquadFilter():null;
  if(filter){filter.type='lowpass';filter.frequency.value=typing?1800:380;filter.Q.value=.35;source.connect(filter);filter.connect(gain);}else source.connect(gain);
  gain.connect(fade);fade.connect(panner);panner.connect(this.effects);
  this.samplePlaybacks.set(source,{...playback,at});
  this.track(source,[gain,fade,panner,...(filter?[filter]:[])],at,at+remaining+.02,time=>source.start(time,offset+elapsed*rate));
 }
 setPaused(paused:boolean){
  if(paused===this.paused)return;this.paused=paused;
  const now=this.context.currentTime;
  if(paused){
   // Capture picture time BEFORE the audible release; those 120ms are never
   // added to the source offset on resume, including visibility changes.
   this.pausedSamples=Array.from(this.samplePlaybacks.values()).map(voice=>({...voice,elapsed:voice.elapsed+Math.max(0,now-voice.at),at:Math.max(0,voice.at-now)})).filter(voice=>voice.elapsed<voice.duration);
   this.samplePlaybacks.clear();this.releaseSources(.12);
  }else{
   const saved=this.pausedSamples;this.pausedSamples=[];
   // A quick resume may precede onended for the old release. Crossfade it once.
   this.releaseSources(.012);
   for(const voice of saved)this.playSample(voice,now+voice.at,true);
  }
 }
 play(cue:SoundCue,at=this.context.currentTime):void{
  if(this.paused||this.disposed)return;
  if(cue.delay)return this.play({...cue,delay:0},at+cue.delay);
  this.currentCue=cue;
  let g=clamp(cue.gain??1,0,1.5);const pan=cue.pan??0,pitch=cue.pitch??1,t=at;
  if(cue.sample){
   const buffer=this.samples.get(cue.sample);if(!buffer)return;
   const offset=clamp(cue.offset??0,0,buffer.duration),available=(buffer.duration-offset)/pitch;
   const duration=cue.duration??Math.min(cue.clipDuration??available,available);
   if(duration<=0)return;
   const rate=cue.duration?available/duration:pitch;
   const foley=cue.sample.startsWith('foley_'),attack=Math.min(cue.fadeIn??(foley?.001:.012),duration*.8),release=Math.min(cue.fadeOut??(foley?.006:.025),duration-attack);
   this.playSample({cue:{...cue},buffer,offset,rate,duration,gain:g,attack,release,elapsed:Math.max(0,cue.elapsed??0),at:t},t);return;
  }
  // Sample cues carry measured source gains. Lift the quiet procedural family
  // independently; the branded launch already has six overlapping voices.
  g*=cue.kind==='start'?1.35:2;
  switch(cue.kind){
   case 'start':
    this.tone(55,'triangle',t,3.5,.15*g,.8,100,0,.2,55,600,2.5,true);
    for(const[f,v,p,lp,to]of [[110,.10,-.3,200,1500],[165,.08,.3,300,2000],[220,.06,0,400,2500]])this.tone(f!,'triangle',t+.2,3,v!*g,1,lp!,p!,.3,f!,to!,2.5,true);
    for(const[f,p]of [[1760,.4],[1765,-.4]])this.tone(f!,'sine',t+1,2,.04*g,.8,6000,p!,.5,f!,6000,.7,true);break;
   case 'tap':this.click(t,.038*g,pan);break;
   case 'stop':this.click(t,.05*g,pan);this.tone(140,'sine',t+.015,.102,.020*g,.002,420,pan,0,80);break;
   case 'check':this.click(t,.05*g,pan);this.tone(1400,'sine',t+.02,.102,.022*g,.002,6000,pan);break;
   case 'cube':
    this.tone(118*pitch,'sine',t,.21,.13*g,.009,420,pan,.018,62*pitch);this.noiseVoice(t,.075,.035*g,500,220,pan,.012,'lowpass');break;
   case 'air':{
    const duration=cue.duration??.85;
    // Sustained flight needs an undecayed source: the old noise buffer and
    // exponential envelope faded twice, leaving most of a long flight silent.
    if(cue.sustain)this.playSample({cue:{...cue},buffer:this.noise(duration,false),offset:0,rate:1,duration,gain:.055*g,attack:duration*.28,release:duration*.42,elapsed:Math.max(0,cue.elapsed??0),at:t},t);
    else this.noiseVoice(t,duration,.10*g,650,260,pan,Math.min(.28,duration*.32),'lowpass');break;
   }
   case 'spring':this.tone(105*pitch,'sine',t,.24,.13*g,.012,380,pan,.02,64*pitch);this.noiseVoice(t,.10,.045*g,420,160,pan,.015,'lowpass');break;
   case 'squash':this.tone(90,'sine',t,.38,.18*g,.02,320,pan,.025,42);this.noiseVoice(t,.16,.06*g,380,160,pan,.02,'lowpass');break;
   case 'recover':this.tone(68,'sine',t,.30,.085*g,.035,320,pan,.015,125);break;
   case 'shutter':
    // A soft camera closure plus a short airy flash, audible on small speakers.
    this.tone(220,'sine',t,.15,.065*g,.012,700,pan,.025,165);
    this.noiseVoice(t+.018,.13,.12*g,1800,850,pan,.025,'lowpass');
    this.noiseVoice(t+.13,.085,.06*g,1300,700,pan,.015,'lowpass');break;
   case 'surprise':
    // The lamp's surprised blink is a voice, not an electrical switch.
    this.tone(740*pitch,'triangle',t,.16,.012*g,.025,1100,pan,.055);break;
   case 'title':this.tone(110,'triangle',t,1.8,.032*g,.45,220,pan,.22,110,680);this.tone(440,'sine',t+.15,1.4,.01*g,.4,1800,-pan,.3);break;
   case 'assemble':this.noiseVoice(t,.65,.025*g,260,1900,pan,.15);this.tone(660*pitch,'sine',t+.18,.75,.026*g,.07,4000,pan,.16);break;
   case 'switch':this.click(t,.022*g,pan);this.tone(440,'sine',t+.035,.65,.009*g,.15,1200,pan,.08);break;
  }
 }
 setAmbience(level:number){
  if(level===this.ambienceLevel)return;const continuous=Math.abs(level-this.ambienceLevel)<.08;this.ambienceLevel=level;
  const ac=this.context;
  // Continuous picture-owned envelopes follow promptly. A direct chapter jump
  // gets a gentle crossfade too; only explicit mute/pause uses the master bus.
  this.ambience.gain.cancelAndHoldAtTime(ac.currentTime);
  if(continuous)this.ambience.gain.setTargetAtTime(clamp(level,0,1),ac.currentTime,.08);
  else this.ambience.gain.linearRampToValueAtTime(clamp(level,0,1),ac.currentTime+2.4);
 }
 setDucking(duck:boolean){if(duck===this.ducked)return;this.ducked=duck;const t=this.context.currentTime;this.effects.gain.cancelAndHoldAtTime(t);this.effects.gain.linearRampToValueAtTime(duck?0:1,t+.18);}
 setMuted(muted:boolean){if(muted===this.muted)return;this.muted=muted;const t=this.context.currentTime;this.master.gain.cancelAndHoldAtTime(t);this.master.gain.linearRampToValueAtTime(muted?0:MASTER_GAIN,t+(muted?.12:.18));}
 clear(release=.015){this.pausedSamples=[];this.samplePlaybacks.clear();this.releaseSources(release);}
 private releaseSources(release:number){const now=this.context.currentTime;for(const source of this.sources){for(const node of this.voiceGains.get(source)??[]){node.gain.cancelAndHoldAtTime(now);node.gain.linearRampToValueAtTime(EPS,now+release);}try{source.stop(now+release+.005);}catch{/* Already ended. */}}}
 get activeVoices(){return this.sources.size;}
 get sourcesReady(){return ([...TRAILER_SOURCES,'L_off_2'] as const).every(key=>this.samples.has(key));}
 destroy(){this.disposed=true;this.requests.abort();this.clear();this.bedModulation.stop();for(const source of this.bed){try{source.stop();}catch{/* Already ended. */}}for(const node of this.nodes)node.disconnect();this.nodes.clear();this.buffers.clear();this.samples.clear();}
}
